import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX, KeyRound, Plus, Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { isApiError } from '../api/client';
import { useApi } from '../api/context';
import { queryKeys, useAllCloudAccounts, useProviders, useSecretStores } from '../api/queries';
import type { CloudAccount, Diagnostic } from '../api/types';
import { useToast } from '../components/toast';
import { ActionButton, Button, Dialog, ErrorMessage, Loading } from '../components/ui';
import { formatDateTime, formatRelative } from '../lib/format';
import { usePhone } from '../shell/breakpoints';
import { strings } from '../strings';
import { CloudAccountDetail } from './CloudAccountPage';
import { CloudAccountForm, ProviderTile, type CloudAccountValue } from './CloudAccountForm';
import { isDiscouraged, methodLabel, PROVIDERS, providerOrder, shortScope } from './providers';

const s = strings.cloudAccounts;

/** The verification state of an account: verified (with the time), failed or never verified (spec 6.6.1). */
export function VerificationBadge({ account }: { account: CloudAccount }) {
  const result = account.lastVerification;
  if (!result) return <span className="badge tone-neutral">{s.never}</span>;
  const at = account.lastVerifiedAt ?? result.checkedAt;
  return result.succeeded ? (
    <span className="badge tone-success" title={formatDateTime(at)}>
      <CircleCheck aria-hidden className="size-3.5" />
      {s.verifiedAgo(formatRelative(at))}
    </span>
  ) : (
    <span className="badge tone-danger" title={formatDateTime(at)}>
      <CircleX aria-hidden className="size-3.5" />
      {s.failed}
    </span>
  );
}

function matches(account: CloudAccount, words: string[]): boolean {
  const text = [
    account.name,
    methodLabel(account.provider, account.auth.method),
    account.auth.method,
    shortScope(account),
    isDiscouraged(account.provider, account.auth.method) ? s.staticKey : '',
  ]
    .join(' ')
    .toLowerCase();
  return words.every((word) => text.includes(word));
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const providers = useProviders();
  const stores = useSecretStores();
  const formId = useId();
  const [busy, setBusy] = useState(false);
  const [verifyAfter, setVerifyAfter] = useState(true);
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);

  const create = async (value: CloudAccountValue) => {
    setBusy(true);
    setDiagnostics([]);
    try {
      const created = await api.createCloudAccount(value);
      if (verifyAfter) {
        try {
          await api.verifyCloudAccount(created.data.id);
        } catch (error) {
          toast.error(error);
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['cloud-accounts'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.cloudAccount(created.data.id) });
      navigate(`/cloud-accounts/${created.data.id}`);
    } catch (error) {
      if (isApiError(error) && error.diagnostics.length) setDiagnostics(error.diagnostics);
      else toast.error(error);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={s.createTitle}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button type="submit" form={formId} disabled={busy} onClick={() => setVerifyAfter(false)}>
            {strings.common.create}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={busy}
            onClick={() => setVerifyAfter(true)}
          >
            {busy ? s.verifying : s.createAndVerify}
          </Button>
        </>
      }
    >
      <CloudAccountForm
        formId={formId}
        providers={providers.data ?? []}
        secretStores={stores.data ?? []}
        readOnly={false}
        diagnostics={diagnostics}
        onSubmit={(value) => void create(value)}
      />
    </Dialog>
  );
}

function AccountList({ accounts, currentId }: { accounts: CloudAccount[]; currentId: string | undefined }) {
  const providers = useProviders();
  const [filter, setFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const filterId = useId();
  const words = filter.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = accounts.filter((a) => matches(a, words));
  const groups = useMemo(() => {
    const keys = providerOrder(shown.map((a) => a.provider));
    return keys.map((key) => ({
      key,
      name: providers.data?.find((p) => p.key === key)?.displayName ?? PROVIDERS[key]?.displayName ?? key,
      items: shown
        .filter((a) => a.provider === key)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    }));
  }, [shown, providers.data]);

  return (
    <div className="ca-list">
      <div className="ca-list-head">
        <h1 className="text-lg font-semibold">{s.title}</h1>
        <ActionButton
          permission="admin"
          variant="primary"
          icon={<Plus aria-hidden className="size-4" />}
          aria-label={s.create}
          onClick={() => setCreating(true)}
        >
          {s.newShort}
        </ActionButton>
      </div>
      <div className="ca-filter">
        <label htmlFor={filterId} className="sr-only">
          {s.filterLabel}
        </label>
        <Search aria-hidden className="size-4 text-fg-faint" />
        <input
          id={filterId}
          className="ca-filter-input"
          type="search"
          placeholder={s.filter}
          data-list-search
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </div>
      {accounts.length === 0 ? (
        <p className="help p-4">{s.empty}</p>
      ) : shown.length === 0 ? (
        <p className="help p-4">{s.noMatch}</p>
      ) : (
        groups.map((group) => (
          <section key={group.key} aria-labelledby={`${filterId}-${group.key}`}>
            <h2 id={`${filterId}-${group.key}`} className="ca-group">
              {group.name}
            </h2>
            <ul>
              {group.items.map((account) => (
                <li key={account.id}>
                  <Link
                    to={`/cloud-accounts/${account.id}`}
                    className="ca-row"
                    aria-current={account.id === currentId ? 'page' : undefined}
                  >
                    <ProviderTile provider={account.provider} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{account.name}</span>
                      <span className="block truncate text-xs text-fg-muted">
                        {methodLabel(account.provider, account.auth.method)}
                        {shortScope(account) && ` · ${shortScope(account)}`}
                      </span>
                    </span>
                    <span className="ca-row-badges">
                      <VerificationBadge account={account} />
                      {isDiscouraged(account.provider, account.auth.method) && (
                        <span className="badge tone-warning">
                          <KeyRound aria-hidden className="size-3" />
                          {s.staticKey}
                        </span>
                      )}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      {creating && <CreateDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/** `/cloud-accounts` and `/cloud-accounts/:id`: the master-detail page (spec 6.6, mock m11). */
export function CloudAccountsPage() {
  const { id } = useParams();
  const accounts = useAllCloudAccounts();
  const phone = usePhone();
  const list = accounts.error ? (
    <ErrorMessage error={accounts.error} />
  ) : !accounts.data ? (
    <Loading />
  ) : (
    <AccountList accounts={accounts.data} currentId={id} />
  );
  // Below 768 px the list and the detail are separate screens (spec 6.6.7).
  if (phone) {
    return <div className="ca-page is-phone">{id ? <CloudAccountDetail id={id} /> : list}</div>;
  }
  return (
    <div className="ca-page">
      <div className="ca-master">{list}</div>
      <div className="ca-detail">
        {id ? <CloudAccountDetail key={id} id={id} /> : <p className="help p-6">{s.choose}</p>}
      </div>
    </div>
  );
}
